import { useEffect, useLayoutEffect, useRef, useState, forwardRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

export interface PopoverProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  open: boolean;
  /** Element the popover hangs off (button, input container…). Without one it renders at the viewport origin. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Called on outside click (excluding the anchor) and Escape. Omit for passive popovers. */
  onClose?: () => void;
  /** Preferred side; flips to the other side when there isn't room. */
  side?: 'top' | 'bottom';
  /** Which anchor edge the popover lines up with. */
  align?: 'start' | 'end' | 'center';
  /** Gap between anchor and popover in px. */
  offset?: number;
  children: React.ReactNode;
}

const VIEWPORT_PAD = 8;

interface Placement { top: number; left: number; side: 'top' | 'bottom' }

function place(anchor: DOMRect, w: number, h: number, side: 'top' | 'bottom', align: 'start' | 'end' | 'center', offset: number): Placement {
  const vw = window.innerWidth, vh = window.innerHeight;
  const below = anchor.bottom + offset;
  const above = anchor.top - offset - h;
  let s = side;
  if (s === 'bottom' && below + h > vh - VIEWPORT_PAD && above >= VIEWPORT_PAD) s = 'top';
  else if (s === 'top' && above < VIEWPORT_PAD && below + h <= vh - VIEWPORT_PAD) s = 'bottom';

  let left = align === 'start' ? anchor.left
    : align === 'end' ? anchor.right - w
    : anchor.left + anchor.width / 2 - w / 2;
  left = Math.min(Math.max(left, VIEWPORT_PAD), Math.max(VIEWPORT_PAD, vw - w - VIEWPORT_PAD));

  return { top: s === 'bottom' ? below : Math.max(VIEWPORT_PAD, above), left, side: s };
}

/**
 * Popover rendered in a portal on document.body with fixed positioning, so it is never
 * clipped by scrolling/overflow-hidden ancestors. Handles outside click + Escape when
 * `onClose` is given, and follows the anchor while the page scrolls or resizes.
 */
export const Popover = forwardRef<HTMLDivElement, PopoverProps>(function Popover(
  { open, anchorRef, onClose, side = 'bottom', align = 'end', offset = 6, className, style, children, ...rest },
  forwardedRef,
) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const setRefs = useCallback((el: HTMLDivElement | null) => {
    innerRef.current = el;
    if (typeof forwardedRef === 'function') forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  }, [forwardedRef]);

  const reposition = useCallback(() => {
    const p = innerRef.current;
    if (!p) return;
    const a = anchorRef?.current?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0);
    setPlacement(place(a, p.offsetWidth, p.offsetHeight, side, align, offset));
  }, [anchorRef, side, align, offset]);

  // Measure + place before paint
  useLayoutEffect(() => {
    if (!open) { setPlacement(null); return; }
    reposition();
  }, [open, reposition]);

  // Follow the anchor on scroll/resize and when the popover's own size changes
  useEffect(() => {
    if (!open) return;
    let raf = 0;
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(reposition); };
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (ro && innerRef.current) ro.observe(innerRef.current);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      ro?.disconnect();
    };
  }, [open, reposition]);

  // Outside click (anchor clicks are left to the anchor's own toggle) + Escape
  useEffect(() => {
    if (!open || !onCloseRef.current) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (innerRef.current?.contains(t) || anchorRef?.current?.contains(t)) return;
      onCloseRef.current?.();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current?.(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={setRefs}
      data-side={placement?.side ?? side}
      style={{
        position: 'fixed',
        top: placement?.top ?? 0,
        left: placement?.left ?? 0,
        // hidden until measured, so it never flashes at 0,0
        visibility: placement ? 'visible' : 'hidden',
        ...style,
      }}
      className={clsx('z-[9000]', className)}
      {...rest}
    >
      {children}
    </div>,
    document.body,
  );
});
